// API base URL - defaults to same origin in production, or localhost in development
const getApiBaseUrl = (): string => {
  const envUrl = import.meta.env.VITE_API_URL;
  if (envUrl) return envUrl;

  // In production, use same origin
  if (import.meta.env.PROD) {
    return '';
  }

  // Default to localhost for development
  return 'http://localhost:8000';
};

export const API_BASE_URL = getApiBaseUrl();

// Custom error class for API errors
export class ApiError extends Error {
  status: number;
  bodyText?: string;

  constructor(message: string, status: number, bodyText?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.bodyText = bodyText;
  }
}

// FastAPI returns `detail` as a string for HTTPException, but as an array of
// `{loc, msg, type, ...}` objects for Pydantic validation errors (HTTP 422).
// This helper coerces either shape (or unknown errors) into a single string
// safe to render or display in a toast.
export function extractApiErrorMessage(
  err: unknown,
  fallback: string
): string {
  if (err instanceof ApiError && err.bodyText) {
    try {
      const parsed = JSON.parse(err.bodyText) as { detail?: unknown };
      const detail = parsed.detail;
      if (typeof detail === 'string' && detail.trim()) {
        return detail;
      }
      if (Array.isArray(detail)) {
        const messages = detail
          .map((item) => {
            if (item && typeof item === 'object' && 'msg' in item) {
              const msg = (item as { msg?: unknown }).msg;
              if (typeof msg === 'string' && msg.trim()) return msg;
            }
            if (typeof item === 'string' && item.trim()) return item;
            return null;
          })
          .filter((m): m is string => Boolean(m));
        if (messages.length > 0) return messages.join('; ');
      }
    } catch {
      // body was not JSON; fall through to fallback
    }
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

function isHtmlErrorBody(body: string): boolean {
  const trimmed = body.trim();
  if (!trimmed) return false;
  const lower = trimmed.slice(0, 256).toLowerCase();
  return (
    lower.startsWith('<!doctype') ||
    lower.startsWith('<html') ||
    (trimmed.startsWith('<') && lower.includes('<body'))
  );
}

/** User-facing API error text; omits HTML/proxy error pages (shows HTTP status instead). */
export function formatApiErrorForDisplay(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.bodyText && !isHtmlErrorBody(err.bodyText)) {
      const detail = extractApiErrorMessage(err, '');
      if (detail.trim()) return detail;
      const trimmed = err.bodyText.trim();
      if (trimmed.length <= 300) return trimmed;
      return `${trimmed.slice(0, 300)}…`;
    }
    return err.status ? `HTTP ${err.status}` : 'HTTP error';
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Request failed';
}

// Type for Clerk's getToken function
type GetToken = () => Promise<string | null>;

type ApiFetchInit = Omit<RequestInit, 'headers'> & {
  headers?: Record<string, string>;
  auth?: boolean;
};

/**
 * The household the web app is currently acting within. Multi-household users
 * can switch this; it's mirrored into every authenticated request via the
 * `X-Household-Id` header so the backend scopes reads/writes to it. Kept as a
 * module-level value (set by the app layout) so callers don't have to thread
 * the id through every call. When unset the backend falls back to the user's
 * oldest household.
 */
export const ACTIVE_HOUSEHOLD_HEADER = 'X-Household-Id';

let activeHouseholdId: number | null = null;

export function setActiveHouseholdId(id: number | null): void {
  activeHouseholdId = id;
}

export function getActiveHouseholdId(): number | null {
  return activeHouseholdId;
}

// Fetch with optional auth token
export async function apiFetch(
  getToken: GetToken,
  path: string,
  init?: ApiFetchInit
): Promise<Response> {
  const url = `${API_BASE_URL}${path}`;
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(init?.headers ?? {}),
  };

  const auth = init?.auth ?? true;
  if (auth) {
    const token = await getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  if (activeHouseholdId != null && headers[ACTIVE_HOUSEHOLD_HEADER] == null) {
    headers[ACTIVE_HOUSEHOLD_HEADER] = String(activeHouseholdId);
  }

  return fetch(url, {
    ...init,
    headers,
  });
}

// Fetch and parse JSON with auth
export async function apiJson<T>(
  getToken: GetToken,
  path: string,
  init?: ApiFetchInit
): Promise<T> {
  const res = await apiFetch(getToken, path, init);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiError(
      `Request failed: ${res.status} ${res.statusText}`,
      res.status,
      text
    );
  }

  // 204 No Content (and other success responses with an empty body)
  if (res.status === 204) return undefined as unknown as T;

  const text = await res.text();
  if (!text.trim()) return undefined as unknown as T;

  return JSON.parse(text) as T;
}

// Legacy function for non-authenticated requests
export async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }

  return response.json();
}
